import React, { useState, useEffect } from 'react';
import { Layout, Menu } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BookOutlined,
  CalendarOutlined,
  FileTextOutlined,
  UserOutlined,
  SolutionOutlined,
  EnvironmentOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HistoryOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useResponsive } from '@/hooks/useResponsive';

const { Sider } = Layout;

/**
 * 应用侧边栏组件
 *
 * 特性:
 * - PC端: 固定220px宽度侧边栏
 * - Tablet端: 可折叠侧边栏
 * - 根据用户角色显示不同菜单项
 */
const AppSider: React.FC = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isTablet, isDesktop } = useResponsive();

  // Tablet端默认折叠
  useEffect(() => {
    if (isTablet) {
      setCollapsed(true);
    } else if (isDesktop) {
      setCollapsed(false);
    }
  }, [isTablet, isDesktop]);

  // 管理员菜单：只关心数据配置（校区/老师/班级/学生）
  const adminMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('nav.dashboard'),
    },
    {
      key: '/campuses',
      icon: <EnvironmentOutlined />,
      label: t('nav.campuses'),
    },
    {
      key: '/teachers',
      icon: <SolutionOutlined />,
      label: t('nav.teachers'),
    },
    {
      key: '/classes',
      icon: <BookOutlined />,
      label: t('nav.classes'),
    },
    {
      key: '/students',
      icon: <TeamOutlined />,
      label: t('nav.students'),
    },
    {
      key: '/data-management',
      icon: <DatabaseOutlined />,
      label: t('nav.dataManagement'),
    },
    {
      key: '/audit-logs',
      icon: <HistoryOutlined />,
      label: t('nav.auditLog'),
    },
  ];

  // 老师菜单：简化为只查看班级和学生
  const teacherMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('nav.dashboard'),
    },
    {
      key: '/classes',
      icon: <BookOutlined />,
      label: t('nav.classes'),
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
    },
  ];

  // 根据角色决定菜单
  const menuItems = user?.role === 'admin'
    ? [
        ...adminMenuItems,
        {
          key: '/profile',
          icon: <UserOutlined />,
          label: t('nav.profile'),
        },
      ]
    : user?.role === 'teacher'
      ? teacherMenuItems
      : []; // 家长走 /parent 路由，不使用此侧边栏

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={220}
      collapsedWidth={isTablet ? 0 : 80}
      style={{
        background: '#fff',
        borderRight: '1px solid #f0f0f0',
        overflow: 'auto',
        height: 'calc(100vh - 64px)',
        position: 'sticky',
        top: 64,
        left: 0,
        display: isTablet && collapsed ? 'none' : 'block',
      }}
    >
      {/* 折叠按钮 (仅Tablet显示) */}
      {isTablet && !collapsed && (
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <MenuFoldOutlined
            onClick={() => setCollapsed(true)}
            style={{ fontSize: '18px', cursor: 'pointer', color: '#666' }}
          />
        </div>
      )}

      {/* 展开按钮 (仅Tablet且已折叠时显示) */}
      {isTablet && collapsed && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#fff',
            padding: '12px 8px',
            borderRadius: '0 8px 8px 0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            zIndex: 100,
          }}
          onClick={() => setCollapsed(false)}
        >
          <MenuUnfoldOutlined style={{ fontSize: '18px', color: '#0ea5e9' }} />
        </div>
      )}

      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 'none',
          paddingTop: '16px',
        }}
      />
    </Sider>
  );
};

export default AppSider;
